import React from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Microscope } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { expertises } from '../data/mock';

const ExpertisesPage = () => {
  const location = useLocation();
  const hash = location.hash.replace('#', '');
  const defaultTab = expertises.find(e => e.id === hash)?.id || expertises[0].id;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 medical-pattern opacity-50" />
        <div className="decorative-circle w-96 h-96 -top-48 -right-48 opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium mb-6">
              <Microscope className="w-4 h-4" />
              <span>Spécialisations</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-gray-900">
              Domaines d'expertise
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              Spécialiste en chirurgie mini-invasive, je prends en charge les pathologies digestives et pariétales en privilégiant les techniques laparoscopiques.
            </p>
          </div>
        </div>
      </section>

      {/* Expertises Tabs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-transparent h-auto p-0 mb-12">
              {expertises.map((expertise, index) => (
                <TabsTrigger
                  key={expertise.id}
                  value={expertise.id}
                  className="px-6 py-3 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-teal-700/20 rounded-xl border border-gray-200 data-[state=active]:border-teal-700 text-gray-600 hover:bg-gray-50 transition-all duration-300"
                >
                  <span className="mr-2 text-xs opacity-60">{String(index + 1).padStart(2, '0')}</span>
                  {expertise.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {expertises.map((expertise) => (
              <TabsContent key={expertise.id} value={expertise.id} id={expertise.id}>
                <div className="grid lg:grid-cols-3 gap-8">
                  {/* Main Description */}
                  <div className="lg:col-span-2 space-y-6">
                    <div className="animate-fade-in-up">
                      <Badge className="mb-4 bg-teal-50 text-teal-700 hover:bg-teal-100">
                        Chirurgie mini-invasive
                      </Badge>
                      <h2 className="text-3xl font-light text-gray-900 mb-6">
                        {expertise.title}
                      </h2>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {expertise.fullDesc}
                      </p>
                    </div>

                    {/* When to Consult */}
                    <Card className="border-0 shadow-md bg-gradient-to-br from-teal-50 to-teal-100/50 overflow-hidden">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-medium text-teal-800 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          Quand consulter ?
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {expertise.whenConsult.map((item, index) => (
                            <li key={index} className="flex items-start gap-3 text-teal-900">
                              <span className="w-6 h-6 rounded-full bg-teal-200 text-teal-800 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                {index + 1}
                              </span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Alternatives */}
                    <Card className="border border-gray-100 shadow-sm hover-lift">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                          </div>
                          Alternatives non chirurgicales
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {expertise.alternatives.map((item, index) => (
                            <li key={index} className="flex items-start gap-3 text-gray-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Not Indicated */}
                    <Card className="border border-gray-100 shadow-sm hover-lift">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <XCircle className="w-4 h-4 text-gray-500" />
                          </div>
                          Cas où la chirurgie n'est pas indiquée
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {expertise.notIndicated.map((item, index) => (
                            <li key={index} className="flex items-start gap-3 text-gray-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sidebar */}
                  <div className="lg:col-span-1">
                    <Card className="border-0 shadow-lg bg-white sticky top-24 overflow-hidden">
                      <div className="h-2 bg-gradient-to-r from-teal-500 to-teal-700" />
                      <CardContent className="p-6">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                          Principe de prise en charge
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">
                          Chaque situation est unique. La décision d'opérer ou non résulte d'une évaluation complète et d'un dialogue avec le patient.
                        </p>
                        <Separator className="my-4" />
                        <p className="text-sm text-gray-600 leading-relaxed mb-6">
                          L'objectif n'est pas d'opérer systématiquement mais de proposer la solution la plus adaptée à votre cas particulier.
                        </p>
                        
                        <div className="p-4 rounded-xl bg-teal-50">
                          <p className="text-sm font-medium text-teal-800 mb-1">
                            Besoin d'un avis ?
                          </p>
                          <p className="text-xs text-teal-700">
                            Contactez le cabinet pour une consultation personnalisée.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </Layout>
  );
};

export default ExpertisesPage;
